import { ReadonlyVec2 } from 'gl-matrix';
import { gl } from '../../Engine';
import { TextureSource } from '../../core/models/TextureSource';
import { Environment } from '../../data/Environment';
import { TextureView } from '../TextureView';

export type LayerBitMask = number;

/**
 * {@link Layer} represents a visualization of specific type of data on a {@link TextureView}.
 * T defines the type of Objects that the {@link Layer} visualizes.
 */
export abstract class Layer<T> implements TextureSource {
    /**
     * The {@link TextureView parent} of this {@link Layer}.
     */
    protected readonly parent: TextureView;

    /**
     * The {@link Environment} in which this {@link Layer} exists.
     */
    protected readonly env: Environment;

    /**
     * {@link WebGLTexture Texture} containing the data rendered on this {@link Layer}.
     * The size of this {@link WebGLTexture texture} should be equal to the size of the {@link Layer} and the {@link TextureView} it resides on.
     */
    private readonly _texture: WebGLTexture;

    /**
     * Constructor to create a layer.
     * @param view The {@link TextureView parent} of this {@link Layer}. Layers may not exist without TextureViews.
     */
    protected constructor(parent: TextureView, env: Environment) {
        this.parent = parent;
        this.env = env;
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error('Unable to create LayerTexture');
        }
        this._texture = texture;
        const size = parent.size;
        if (size[0] !== 0 && size[1] !== 0) {
            this.resize(size);
        }
    }

    /**
     * Method to inform the {@link Layer} that the {@link view View}'s {@link TextureView.size size} has changed.\
     * As {@link Layer} should always be the same size as the {@link TextureView View}, we must also update the Layer's {@link size}.
     *
     * Re-allocates the texture and leaves it undrawn. Filling it is the
     * caller's responsibility, and requires a bound framebuffer, which this
     * method neither establishes nor assumes: {@link TextureView.resize}
     * follows this with {@link TextureView.updateLayers}, which binds one and
     * draws every layer.
     * @param size {@link TextureView View's} new size in pixels.
     * @returns Reference to this.
     */
    public resize(size: ReadonlyVec2): Layer<T> {
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            this.internalFormat,
            size[0],
            size[1],
            0,
            this.format,
            gl.UNSIGNED_BYTE,
            null
        );
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.bindTexture(gl.TEXTURE_2D, null);
        return this;
    }

    /**
     * The internal format of the {@link Layer._texture Layer texture}.
     * For valid values see https://registry.khronos.org/OpenGL-Refpages/gl4/html/glTexImage2D.xhtml
     */
    public abstract get internalFormat(): number;

    /**
     * The format of the Layer texture. One of:
     * GL_RED, GL_RG, GL_RGB, GL_BGR, GL_RGBA, GL_BGRA,
     * GL_RED_INTEGER, GL_RG_INTEGER, GL_RGB_INTEGER, GL_BGR_INTEGER, GL_RGBA_INTEGER, GL_BGRA_INTEGER,
     * GL_STENCIL_INDEX, GL_DEPTH_COMPONENT or GL_DEPTH_STENCIL
     */
    public abstract get format(): number;

    /**
     * Every {@link Layer}-class must define their own {@link layerBit}.\
     * LayerBit is used by {@link ViewInvalidator.invalidate invalidations} to select which {@link Layer Layers} need to be updated.
     */
    public abstract get layerBit(): LayerBitMask;

    /**
     * Abstract method to retrieve all the targets from the {@link Environment}.
     * @param environment {@link Environment} from which the targets are to be retrieved.
     * @returns Array of objects of requested type found in the {@link Environment}.
     */
    public abstract getTargets(environment: Environment): T[];

    /**
     * Method to draw the given targets onto this {@link Layer Layer's} {@link Layer.texture texture}.\
     * Implementations are expected to start with {@link bindAndClearTexture},
     * so that stale data is removed even when there is nothing to draw.
     * @param targets The objects of the datatype this {@link Layer} visualizes, as returned by {@link getTargets}.
     */
    protected abstract draw(targets: T[]): void;

    /**
     * Binds {@link texture} on the currently bound {@link FrameBufferObject}
     * and clears it to transparent: how every {@link draw} starts.
     *
     * The clear colour is set here rather than inherited from whatever
     * off-screen pass last wrote that context-wide state. The view
     * composites each layer over the one below by its alpha, so a layer
     * cleared opaque does not merely tint itself: it hides every layer
     * under it wherever it draws nothing.
     */
    protected bindAndClearTexture(): void {
        gl.framebufferTexture2D(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            gl.TEXTURE_2D,
            this._texture,
            0
        );
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
    }

    /**
     * Method to erase the {@link Layer.texture texture} allocated by this {@link Layer}.
     * @returns True to indicate that the {@link Layer} and all of it's contents were deleted\
     * and it's WebGL memory de-allocated.
     */
    public delete(): boolean {
        gl.deleteTexture(this.texture);
        return true;
    }

    /**
     * Method to update the {@link Layer.texture Layer's texture} by {@link draw drawing} the
     * {@link getTargets targets} currently present in the {@link Environment}.
     * @param env {@link Environment} from which the targets are retrieved.
     * @returns True if the {@link Layer} was updated. False otherwise.
     */
    public update(env: Environment): boolean {
        this.draw(this.getTargets(env));
        return true;
    }

    /**
     * Method to get reference to this {@link Layer}'s parent {@link TextureView}.
     * @returns Reference to this {@link Layer}'s parent.
     */
    public getView(): TextureView {
        return this.parent;
    }

    public get texture(): WebGLTexture {
        return this._texture;
    }
}

/**
 * Type that represents {@link Layer} constructor.
 */
export type LayerClass<T> = new (
    view: TextureView,
    env: Environment
) => Layer<T>;
